// Central translator for backend `AltivError`-shaped responses.
//
// Backend (Sprint 1.4 + Fase 5) emits errors as:
//   { code, message, details? }
// Where `code` is a stable machine identifier ("trip_not_found",
// "schema_validation_failed", "foreign_company", …) that maps 1:1 to
// the `apiErrors.<code>` key in `i18n/{es,en}.json`.
//
// Pages should call `apiErrorMessage(err, t)` instead of reading
// `err.response?.data?.detail` directly — this way every surface speaks
// the same language and gets the same fallback chain when the backend
// returns something unexpected.
//
// Fallback order:
//   1. i18n string for the `code` (e.g. "apiErrors.trip_not_found")
//   2. backend `message` (still English — better than nothing)
//   3. generic "apiErrors.fallback"

const I18N_NS = "apiErrors";

function _extractPayload(err) {
  // Axios shape: err.response.data is the parsed JSON body.
  const data = err?.response?.data;
  if (!data || typeof data !== "object") return null;

  // Direct shape (current AltivError handler): {code, message, details?}
  if (typeof data.code === "string") return data;

  // FastAPI default HTTPException wraps it inside `detail`:
  //   {"detail": {"code": "...", "message": "..."}}
  // Defensive — happens if a route raises HTTPException with our dict.
  if (data.detail && typeof data.detail === "object" && typeof data.detail.code === "string") {
    return data.detail;
  }

  return null;
}

/**
 * Translate a backend AltivError-shaped response into a user-facing string.
 *
 * @param {*} err     The axios rejection (or any object with `response.data`).
 * @param {Function} t i18next translator (`useTranslation().t`).
 * @returns {string}   Translated, user-safe message.
 */
export function apiErrorMessage(err, t) {
  // Network-level: no response from server.
  if (err?.isNetworkError || (err && !err.response)) {
    return t(`${I18N_NS}.network`);
  }

  const payload = _extractPayload(err);
  if (payload?.code) {
    const key = `${I18N_NS}.${payload.code}`;
    const translated = t(key);
    // i18next returns the key itself when the lookup misses — treat
    // that as "no entry" and fall through to the backend message.
    if (translated && translated !== key) return translated;
    if (typeof payload.message === "string" && payload.message) return payload.message;
  }

  return t(`${I18N_NS}.fallback`);
}

/**
 * Extract the raw `code` (without translating) — useful for branching
 * (e.g. show retry button only on schema_validation_failed). Returns
 * `null` if the response is not AltivError-shaped.
 *
 * @param {*} err  Axios rejection.
 * @returns {string|null}
 */
export function apiErrorCode(err) {
  const payload = _extractPayload(err);
  return payload?.code || null;
}

/**
 * Extract the `details` object — useful when a page wants to surface
 * which fields failed (schema_validation_failed.errors, foreign_company.resource).
 *
 * @param {*} err  Axios rejection.
 * @returns {object|null}
 */
export function apiErrorDetails(err) {
  const payload = _extractPayload(err);
  return payload?.details || null;
}
